import api, { isSuccess } from "util/api";
import { getGamePoints } from "./getPoints";
import { getGameDescription } from "./helpers";
import { GameAnswer } from "./types/GameAnswers";
import { GameConfigurationOnSubmit } from "./types/GameConfiguration";
import { GameQuestion } from "./types/GameQuestions";
import {
  ApiPostGameQuestionMetaData,
  ApiPostGameQuestionParams,
  ApiPostGameQuestionResponse,
  apiPostGameQuestionPath,
} from "apiRoutes/apiQuestionRoutes";
import { GameDescription } from "./allTechniqueGameDescriptions";
import { CompletedTodo } from "shared/todos/CompletedTodo";
import {
  ApiCompletedTodoCreateParams,
  ApiCompletedTodoCreateResponse,
  apiCompletedTodoCreatePath,
} from "apiRoutes/apiCompletedTodoRoutes";

interface QuestionFormatParams<Q extends GameQuestion, A extends GameAnswer> {
  question: Q;
  answer: A;
  game: GameDescription;
  pointsEarned: number;
}

const generateOnSubmitHandler =
  <
    Q extends GameQuestion,
    A extends GameAnswer,
    Metadata extends ApiPostGameQuestionMetaData
  >(
    questionFormatter: (
      params: QuestionFormatParams<Q, A>
    ) => ApiPostGameQuestionParams<Metadata>["question"]
  ): GameConfigurationOnSubmit<Q, A> =>
  async (question, answer, auth) => {
    const { difficulty } = question;
    const game = getGameDescription(question.type);
    const pointsEarned = getGamePoints({
      auth,
      difficulty,
      correct: answer.correct,
      game: question.type,
    });

    let completedTodos: CompletedTodo[] | null = null;

    if (question.todoId !== null && answer.correct) {
      const nextCompletedTodos = await api<
        ApiCompletedTodoCreateResponse,
        ApiCompletedTodoCreateParams
      >(apiCompletedTodoCreatePath, "POST", {
        body: { todo_id: question.todoId },
      });

      if (isSuccess(nextCompletedTodos)) {
        completedTodos = nextCompletedTodos.response;
      } else {
        alert("Something went wrong. Please try again later.");
      }
    }

    const user = await api<
      ApiPostGameQuestionResponse,
      ApiPostGameQuestionParams<Metadata>
    >(apiPostGameQuestionPath, "POST", {
      body: {
        round: { unique_identifier: question.roundId },
        question: questionFormatter({ question, answer, game, pointsEarned }),
      },
    });

    if (!isSuccess(user)) {
      alert("Something went wrong. Please try again later.");
    }

    return {
      pointsEarned,
      user: isSuccess(user) ? user.response : null,
      completedTodos,
    };
  };

export default generateOnSubmitHandler;
